/**
 * Sysadmin Studio Catalog → Prompts (scaffolding).
 *
 * Lists system_prompts + Sheet-based create/edit. Polished UI is v1.2.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, MessageSquare, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSysAdminSystemPrompts,
  useCreateSystemPrompt,
  useUpdateSystemPrompt,
  useDeleteSystemPrompt,
  type SystemPrompt,
} from '@/lib/use-sysadmin-system-catalog';

export const Route = createFileRoute('/sysadmin/studio/prompts')({
  component: SysadminSystemPromptsPage,
});

interface FormState {
  slug: string;
  displayName: string;
  description: string;
  template: string;
  variablesText: string;
  category: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  slug: '', displayName: '', description: '', template: '',
  variablesText: '[]', category: 'general', isActive: true,
};

function rowToForm(r: SystemPrompt): FormState {
  return {
    slug: r.slug,
    displayName: r.displayName,
    description: r.description,
    template: r.template,
    variablesText: JSON.stringify(r.variables ?? [], null, 2),
    category: r.category,
    isActive: r.isActive,
  };
}

function SysadminSystemPromptsPage() {
  const { data, isLoading, error } = useSysAdminSystemPrompts();
  const create = useCreateSystemPrompt();
  const [editing, setEditing] = useState<SystemPrompt | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SystemPrompt | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (creating) setForm(EMPTY);
    else if (editing) setForm(rowToForm(editing));
  }, [creating, editing]);

  const update = useUpdateSystemPrompt(editing?.id ?? '');
  const del = useDeleteSystemPrompt();

  const items = useMemo(() => data?.items ?? [], [data]);
  const sheetOpen = creating || !!editing;
  const closeSheet = () => { setCreating(false); setEditing(null); };

  const handleSave = () => {
    if (!form.slug.trim() || !/^[a-z0-9_.-]+$/.test(form.slug.trim())) return toast.error('Invalid slug');
    if (!form.displayName.trim() || !form.description.trim() || !form.template.trim()) return toast.error('Display name, description, and template are required.');
    let variables: unknown[];
    try {
      variables = JSON.parse(form.variablesText || '[]');
      if (!Array.isArray(variables)) throw new Error('variables must be a JSON array');
    } catch (e) { return toast.error(`variables invalid: ${(e as Error).message}`); }

    const body: Partial<SystemPrompt> = {
      slug: form.slug.trim(),
      displayName: form.displayName.trim(),
      description: form.description.trim(),
      template: form.template,
      variables: variables as SystemPrompt['variables'],
      category: form.category.trim() || 'general',
      isActive: form.isActive,
    };

    if (creating) {
      create.mutate(body, {
        onSuccess: () => { toast.success(`Prompt "${body.slug}" created`); closeSheet(); },
        onError: (err) => toast.error('Create failed', { description: err.message }),
      });
    } else if (editing) {
      const { slug, ...rest } = body;
      void slug;
      update.mutate(rest, {
        onSuccess: () => { toast.success(`Prompt "${editing.slug}" updated`); closeSheet(); },
        onError: (err) => toast.error('Update failed', { description: err.message }),
      });
    }
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    del.mutate({ id: confirmDelete.id }, {
      onSuccess: () => { toast.success(`Prompt "${confirmDelete.slug}" deactivated`); setConfirmDelete(null); },
      onError: (err) => toast.error('Delete failed', { description: err.message }),
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            System Prompts
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Sysadmin-authored slash-command templates. Tenants can clone a system prompt into a tenant-owned editable copy (Q3).
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" /> New prompt
        </Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground animate-pulse">Loading…</CardContent></Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No system prompts yet.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-24">Vars</TableHead>
                  <TableHead className="w-24">Category</TableHead>
                  <TableHead className="w-20">Active</TableHead>
                  <TableHead className="w-16">Ver</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-accent/30" onClick={() => setEditing(p)}>
                    <TableCell className="font-mono text-xs">{p.slug}</TableCell>
                    <TableCell className="text-sm">{p.displayName}</TableCell>
                    <TableCell className="text-xs tabular-nums">{p.variables.length}</TableCell>
                    <TableCell className="text-xs">{p.category}</TableCell>
                    <TableCell><Badge variant={p.isActive ? 'secondary' : 'outline'} className="text-[10px]">{p.isActive ? 'Active' : 'Inactive'}</Badge></TableCell>
                    <TableCell className="text-xs tabular-nums">v{p.version}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(p)} title="Deactivate">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Sheet open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{creating ? 'New system prompt' : `Edit "${editing?.slug}"`}</SheetTitle>
            <SheetDescription>Use {`{{variable}}`} placeholders in the template — they resolve from the variable definitions when the user invokes the slash command.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!creating} className="font-mono text-xs h-8" placeholder="system.shift-report" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Display name</Label>
              <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} className="text-sm h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Template body</Label>
              <Textarea value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} rows={8} className="text-xs font-mono" spellCheck={false} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Variables (JSON array of {'{name,label,type,required,default,options?}'})</Label>
              <Textarea value={form.variablesText} onChange={(e) => setForm({ ...form, variablesText: e.target.value })} rows={5} className="text-xs font-mono" spellCheck={false} />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="text-sm h-8" />
              </div>
              <div className="flex items-center gap-2 h-8">
                <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} />
                <span className="text-xs">Active</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={closeSheet}>Cancel</Button>
              <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
                {creating ? (create.isPending ? 'Creating…' : 'Create prompt') : (update.isPending ? 'Saving…' : 'Save changes')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate system prompt</AlertDialogTitle>
            <AlertDialogDescription>
              Soft-deletes <code className="bg-muted px-1 rounded">{confirmDelete?.slug}</code>. Tenants stop seeing it. Existing tenant clones are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDelete}>Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
